import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Globe } from "lucide-react";
import { getNationRivalry } from "@/lib/social.functions";
import { trackEvent } from "@/lib/audit";
import { emitPeek } from "@/lib/retention/homeFeedAutoPeek";

const FLAGS: Record<string, string> = {
  MEX: "🇲🇽", ARG: "🇦🇷", BRA: "🇧🇷", FRA: "🇫🇷", ESP: "🇪🇸", USA: "🇺🇸",
  ENG: "🏴", GER: "🇩🇪", ITA: "🇮🇹", POR: "🇵🇹", URU: "🇺🇾", COL: "🇨🇴",
};

/**
 * WorldStatusSurfaces — rotating one-line capsule describing the
 * current world-cup emotional landscape. Reuses Nation Rivalry only.
 * NO new polling beyond the existing rivalry query (deduped by key).
 */
export function WorldStatusSurface({ className = "" }: { className?: string }) {
  const q = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });

  const ranked = useMemo(() => {
    const rows = (q.data?.rows ?? []) as Array<{
      nation_code: string; hype_total: number; active_fans: number;
    }>;
    return rows
      .map((r) => ({
        code: String(r.nation_code),
        total: Number(r.hype_total ?? 0),
        fans: Number(r.active_fans ?? 0),
      }))
      .sort((a, b) => b.total - a.total);
  }, [q.data]);

  const headline = useMemo(() => buildHeadline(ranked), [ranked]);
  const lastHeadline = useRef<string>("");

  useEffect(() => {
    if (!headline.text || lastHeadline.current === headline.text) return;
    lastHeadline.current = headline.text;
    void trackEvent("world_status_surface_shown", { kind: headline.kind, text: headline.text });
    emitPeek("world_status");
  }, [headline.text, headline.kind]);

  if (!headline.text) return null;

  return (
    <div
      className={`relative flex items-center gap-2 overflow-hidden rounded-full border border-border/60 bg-background/70 px-3 py-1.5 text-[11px] backdrop-blur-md ${className}`}
      style={{ boxShadow: `0 0 0 1px color-mix(in oklch, ${headline.color} 22%, transparent)` }}
      aria-label={`World status: ${headline.text}`}
    >
      <Globe className="h-3 w-3" style={{ color: headline.color }} />
      <span
        className="font-black uppercase tracking-[0.18em]"
        style={{ color: headline.color }}
      >
        Mundial
      </span>
      <span className="mx-1 hidden text-muted-foreground sm:inline">·</span>
      <span className="truncate text-foreground/90">
        {headline.flag && <span aria-hidden className="mr-1">{headline.flag}</span>}
        {headline.text}
      </span>
    </div>
  );
}

function buildHeadline(
  ranked: Array<{ code: string; total: number; fans: number }>,
): { text: string; color: string; kind: string; flag?: string } {
  if (ranked.length === 0) return { text: "", color: "oklch(0.7 0.18 250)", kind: "idle" };
  const top = ranked[0];
  const totalAll = ranked.reduce((a, b) => a + b.total, 0);
  const share = totalAll > 0 ? top.total / totalAll : 0;

  if (top.total >= 1500) {
    return {
      text: "Caos global detectado en el Mundial emocional",
      color: "oklch(0.62 0.28 20)",
      kind: "global_chaos",
      flag: FLAGS[top.code],
    };
  }
  if (top.total >= 600 || share >= 0.55) {
    return {
      text: `${top.code} lidera el Mundial emocional`,
      color: "oklch(0.78 0.2 60)",
      kind: "nation_lead",
      flag: FLAGS[top.code],
    };
  }
  if (top.total >= 200) {
    return {
      text: `Fan storm mundial activo desde ${top.code}`,
      color: "oklch(0.7 0.27 330)",
      kind: "fan_storm",
      flag: FLAGS[top.code],
    };
  }
  return {
    text: `${top.code} controla el momentum global`,
    color: "oklch(0.7 0.18 250)",
    kind: "momentum",
    flag: FLAGS[top.code],
  };
}